import React, { useState, useEffect } from 'react';
import userService, { User } from '../../services/userService';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UserForm from './UserForm';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });

  // Filtros
  const [search, setSearch] = useState('');
  const [rol, setRol] = useState('');
  const [activo, setActivo] = useState('true'); // Por defecto mostrar solo activos

  // Modales
  const [openForm, setOpenForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Cargar usuarios
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (search) params.search = search;
      if (rol) params.rol = rol;
      if (activo !== '') params.active = activo;

      const response = await userService.list(params);

      setUsers(response.users);
      setPagination({
        total: response.pagination.total,
        page: response.pagination.page,
        limit: response.pagination.limit,
        pages: response.pagination.pages
      });
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.limit, search, rol, activo]);

  // Handlers
  const handleOpenCreate = () => {
    setSelectedUser(null);
    setOpenForm(true);
  };

  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedUser(null);
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    fetchUsers();
  };

  const handleOpenDelete = (user: User) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      
      await userService.delete(userToDelete.id);
      
      // Cerrar el diálogo
      handleCloseDelete();
      
      // Actualizar el usuario en la lista como inactivo (soft delete)
      setUsers(prev =>
        prev.map(u =>
          u.id === userToDelete.id ? { ...u, active: false } : u
        )
      );
      
    } catch (err: any) {
      console.error('Error al eliminar usuario:', err);
      setError(err.response?.data?.error || 'Error al eliminar usuario');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const response = await userService.toggleStatus(user.id);
      
      // Actualizar el usuario en la lista local
      setUsers(prev =>
        prev.map(u =>
          u.id === user.id
            ? { ...u, active: response.active }
            : u
        )
      );
      
      setError(null);
      
    } catch (err: any) {
      console.error('Error al cambiar estado del usuario:', err);
      setError(err.response?.data?.error || 'Error al cambiar estado del usuario');
    }
  };

  const handleViewDetail = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  const handleAssignBusiness = (user: User) => {
    navigate(`/users/${user.id}/assign`);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearch('');
    setRol('');
    setActivo('true');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 1
    }));
  };

  const getRoleSx = (rol: string) => {
    switch (rol) {
      case 'admin':
        return { bgcolor: '#000000', color: '#FFEB5D', fontWeight: 600 };
      case 'manager':
        return { bgcolor: '#FFEB5D', color: '#000000', fontWeight: 600 };
      case 'auditor':
        return { bgcolor: '#9E9E9E', color: '#000000', fontWeight: 600 };
      default:
        return {};
    }
  };

  const getRoleLabel = (rol: string) => {
    switch (rol) {
      case 'admin':
        return 'Admin';
      case 'auditor':
        return 'Auditor';
      case 'manager':
        return 'Gerente';
      default:
        return rol;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h4" component="h1" sx={{ 
          fontSize: { xs: '1.25rem', sm: '2.125rem' },
          flexShrink: 1,
          minWidth: 0,
        }}>
          Gestión de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
          onClick={handleOpenCreate}
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            py: { xs: 0.75, sm: 1.5 },
            px: { xs: 1.5, sm: 3 },
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          Nuevo Usuario
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="rol-filter-label" htmlFor="rol-filter-input">Rol</InputLabel>
            <Select
              labelId="rol-filter-label"
              id="rol-filter"
              value={rol}
              label="Rol"
              onChange={(e) => {
                setRol(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              inputProps={{
                name: 'rol',
                id: 'rol-filter-input',
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="auditor">Auditor</MenuItem>
              <MenuItem value="manager">Gerente</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="estado-filter-label" htmlFor="estado-filter-input">Estado</InputLabel>
            <Select
              labelId="estado-filter-label"
              id="estado-filter"
              value={activo}
              label="Estado"
              onChange={(e) => {
                setActivo(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              inputProps={{
                name: 'estado',
                id: 'estado-filter-input',
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Activos</MenuItem>
              <MenuItem value="false">Inactivos</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleClearFilters}
          >
            Limpiar
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Rol</TableCell>
                <TableCell align="center">Negocios</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No se encontraron usuarios
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {user.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getRoleLabel(user.role)}
                        size="small"
                        sx={getRoleSx(user.role)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {user.role === 'admin' ? (
                        <Chip
                          label="Acceso Total"
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      ) : (
                        <Chip
                          icon={<BusinessIcon />}
                          label={user.assigned_businesses}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.active ? 'Activo' : 'Inactivo'}
                        color={user.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => handleViewDetail(user)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleOpenEdit(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {user.role !== 'admin' && (
                        <Tooltip title="Asignar negocios">
                          <IconButton size="small" color="info" onClick={() => handleAssignBusiness(user)}>
                            <BusinessIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {user.active ? (
                        <Tooltip title="Desactivar">
                          <IconButton size="small" color="error" onClick={() => handleOpenDelete(user)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Reactivar">
                          <IconButton size="small" color="success" onClick={() => handleToggleStatus(user)}>
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          SelectProps={{
            inputProps: {
              name: 'rows-per-page',
              id: 'pagination-rows-per-page'
            }
          }}
        />
      </Paper>

      {/* Modal de formulario */}
      <UserForm
        open={openForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        user={selectedUser}
      />

      {/* Dialog de confirmación de eliminación */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onCancel={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        title="Desactivar Usuario"
        message={`¿Estás seguro de que deseas desactivar al usuario "${userToDelete?.name}"? Podrás reactivarlo cuando quieras.`}
        loading={deleting}
      />
    </Box>
  );
};

export default UserList;
